import { supabase } from './supabase';

/**
 * Upload an image to Supabase Storage for articles
 * @param file - The image file to upload
 * @param articleSlug - The article slug for naming
 * @returns Public URL of the uploaded image
 */
export const uploadArticleImage = async (
  file: File,
  articleSlug: string
): Promise<string> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${articleSlug}-${Date.now()}.${fileExt}`;
  const filePath = `articles/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('article-images')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading image:', uploadError);
    throw new Error(`Failed to upload image: ${uploadError.message}`);
  }

  const { data: { publicUrl } } = supabase.storage
    .from('article-images')
    .getPublicUrl(filePath);

  return publicUrl;
};

/**
 * Delete an image from Supabase Storage
 * @param imageUrl - The full public URL of the image
 */
export const deleteArticleImage = async (imageUrl: string): Promise<void> => {
  // Extract the file path from the URL
  const url = new URL(imageUrl);
  const pathMatch = url.pathname.match(/article-images\/(.+)/);
  
  if (!pathMatch) {
    console.warn('Could not extract path from URL:', imageUrl);
    return;
  }

  const filePath = pathMatch[1];

  const { error } = await supabase.storage
    .from('article-images')
    .remove([filePath]);

  if (error) {
    console.error('Error deleting image:', error);
    throw new Error(`Failed to delete image: ${error.message}`);
  }
};

/**
 * Upload an image to Supabase Storage for awards
 * @param file - The image file to upload
 * @param slugBase - The award slug base for naming
 * @returns Public URL of the uploaded image
 */
export const uploadAwardImage = async (
  file: File,
  slugBase: string
): Promise<string> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${slugBase}-${Date.now()}.${fileExt}`;
  const filePath = `awards/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('article-images')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading award image:', uploadError);
    throw new Error(`Failed to upload image: ${uploadError.message}`);
  }

  const { data: { publicUrl } } = supabase.storage
    .from('article-images')
    .getPublicUrl(filePath);

  return publicUrl;
};

/**
 * Extract all image URLs from HTML content
 * @param htmlContent - The HTML content to parse
 * @returns Array of image URLs
 */
export const extractImagesFromContent = (htmlContent: string): string[] => {
  const imgRegex = /<img[^>]+src="([^"]+)"/g;
  const matches: string[] = [];
  let match;

  while ((match = imgRegex.exec(htmlContent)) !== null) {
    matches.push(match[1]);
  }

  return matches;
};
